<template>
  <div>
    <h2 v-if="!compact" class="mc-h2">Who else was involved?</h2>
    <p v-if="!compact" class="mc-lede">
      Add passengers, pedestrians or anyone injured. Witnesses go at the bottom.
    </p>

    <div v-if="value.people.length">
      <div v-for="(p, i) in value.people" :key="i" class="mc-card">
        <div class="mc-cardhead">
          <b
            >{{ p.name
            }}<span v-if="p.age" class="mc-age"> ({{ p.age }})</span></b
          >
          <span class="mc-tag" :class="tagClass(p.injured)">{{
            p.injured || 'Not injured'
          }}</span>
        </div>
        <div class="mc-kv">
          <span class="mc-k">Role</span
          ><span class="mc-v">{{ p.role || 'Passenger' }}</span>
        </div>
        <div v-if="p.hospital" class="mc-kv">
          <span class="mc-k">Hospital / treatment</span
          ><span class="mc-v">{{ p.hospital }}</span>
        </div>
        <div class="mc-row-actions">
          <button
            type="button"
            class="mc-btn mc-btn--ghost mc-sm mc-flex1"
            @click="openEdit(i)"
          >
            Edit
          </button>
          <button
            type="button"
            class="mc-btn mc-btn--ghost mc-sm mc-danger"
            @click="deletePerson(i)"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
    <div v-else class="mc-empty">
      <b>No one added yet</b>That's fine if you were alone.
    </div>

    <button
      type="button"
      class="mc-btn mc-btn--sec mc-wide mc-mt16"
      @click="openEdit(-1)"
    >
      ＋ Add a person
    </button>

    <h3 class="mc-h3">Witness</h3>
    <div class="mc-row2">
      <div class="mc-field">
        <label class="mc-lbl" for="mc-wn">Witness name</label>
        <input
          id="mc-wn"
          type="text"
          placeholder="Anyone who saw it happen"
          :value="value.witnessName"
          @input="update('witnessName', $event.target.value)"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="mc-wp">Witness contact</label>
        <input
          id="mc-wp"
          type="tel"
          inputmode="tel"
          placeholder="07XX XXX XXX"
          :value="value.witnessPhone"
          @input="update('witnessPhone', $event.target.value)"
        />
      </div>
    </div>

    <bottom-sheet :open="sheetOpen" @close="closeSheet">
      <h2 class="mc-h2">
        {{ editingIndex >= 0 ? 'Edit person' : 'Add a person' }}
      </h2>
      <div class="mc-field">
        <label class="mc-lbl" for="person-name"
          >Full name <span class="mc-req">*</span></label
        ><input
          id="person-name"
          type="text"
          placeholder="Full name"
          v-model="draft.name"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="person-age">Age</label
        ><input
          id="person-age"
          type="number"
          inputmode="numeric"
          placeholder="29"
          v-model="draft.age"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="person-role">Role</label>
        <select id="person-role" v-model="draft.role">
          <option v-for="r in ROLES" :key="r">{{ r }}</option>
        </select>
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="person-injury">Injury</label>
        <select id="person-injury" v-model="draft.injured">
          <option v-for="r in INJURY" :key="r">{{ r }}</option>
        </select>
      </div>
      <div v-if="draft.injured !== 'Not injured'" class="mc-field">
        <label class="mc-lbl" for="person-hospital">Hospital / treatment</label>
        <input
          id="person-hospital"
          type="text"
          placeholder="e.g. Avenue Hospital"
          v-model="draft.hospital"
        />
      </div>
      <button
        type="button"
        class="mc-btn mc-btn--pri mc-wide"
        @click="savePerson"
      >
        {{ editingIndex >= 0 ? 'Save changes' : 'Add person' }}
      </button>
      <button
        type="button"
        class="mc-btn mc-btn--ghost mc-wide mc-mt8"
        @click="closeSheet"
      >
        Cancel
      </button>
    </bottom-sheet>
  </div>
</template>

<script>
import BottomSheet from '../shell/BottomSheet.vue'

const ROLES = [
  'Passenger in my vehicle',
  'Passenger in another vehicle',
  'Pedestrian',
  'Cyclist / boda rider',
  'Other driver',
  'Other',
]
const INJURY = ['Not injured', 'Injured', 'Injury unknown']
const blankPerson = () => ({
  name: '',
  age: '',
  role: ROLES[0],
  injured: INJURY[0],
  hospital: '',
})

export default {
  name: 'PeopleStep',
  components: { BottomSheet },
  props: {
    // { people: [], witnessName, witnessPhone }
    value: { type: Object, required: true },
    // rendered inside a review card, which already supplies the heading
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      ROLES,
      INJURY,
      sheetOpen: false,
      editingIndex: -1,
      draft: blankPerson(),
    }
  },
  methods: {
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },
    tagClass(injured) {
      if (injured === 'Injured') return 'is-warn'
      if (injured === 'Injury unknown') return ''
      return 'is-ok'
    },
    openEdit(i) {
      this.editingIndex = i
      this.draft = i >= 0 ? { ...this.value.people[i] } : blankPerson()
      this.sheetOpen = true
    },
    closeSheet() {
      this.sheetOpen = false
    },
    savePerson() {
      const o = { ...this.draft, name: this.draft.name.trim() }
      if (o.name.length < 2) {
        this.$emit('toast', 'Please enter a name')
        return
      }
      const people = this.value.people.slice()
      if (this.editingIndex >= 0) people[this.editingIndex] = o
      else people.push(o)
      this.update('people', people)
      this.closeSheet()
      this.$emit('toast', this.editingIndex >= 0 ? 'Updated' : 'Person added')
    },
    deletePerson(i) {
      const people = this.value.people.slice()
      people.splice(i, 1)
      this.update('people', people)
    },
  },
}
</script>

<style scoped>
.mc-h2 {
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #14142b;
}

.mc-h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 22px 0 10px;
  color: #3d4450;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-req {
  color: #eb5757;
}

.mc-field {
  margin: 0 0 16px;
}

@media (min-width: 900px) {
  .mc-row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .mc-row2 .mc-field {
    margin-bottom: 0;
  }
}

.mc-lbl {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #3d4450;
  margin-bottom: 7px;
}

input,
select {
  width: 100%;
  font-family: inherit;
  font-size: 16px;
  color: #333333;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 13px 14px;
  appearance: none;
}

input:focus,
select:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-wide {
  width: 100%;
}

.mc-flex1 {
  flex: 1;
}

.mc-mt8 {
  margin-top: 8px;
}

.mc-mt16 {
  margin-top: 16px;
}

.mc-sm {
  padding: 11px 14px;
  font-size: 14px;
}

.mc-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
}

.mc-cardhead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.mc-cardhead b {
  font-size: 14.5px;
  font-weight: 700;
  color: #14142b;
}

.mc-age {
  font-weight: 500;
  color: #6e7191;
}

.mc-tag {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: #eff0f6;
  /* darker than the usual #6e7191 secondary text — needed for contrast
     against this tinted chip background */
  color: #4e4b66;
}

/* solid colours (pre-blended over white) instead of rgba tints — a flat
   contrast check can't resolve alpha-over-unknown-parent and misreads the
   raw translucent value as darker than what actually renders */
.mc-tag.is-ok {
  background: #e5f5ec;
  color: #166534;
}

.mc-tag.is-warn {
  background: #fdf0e4;
  color: #92400e;
}

.mc-row-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.mc-danger {
  flex: 0 0 auto;
  color: #eb5757;
}

.mc-kv {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 8px 0;
  border-bottom: 1px dashed #e8e8e8;
  font-size: 13.5px;
}

.mc-kv:last-child {
  border-bottom: 0;
}

.mc-k {
  color: #6e7191;
  flex: 0 0 42%;
}

.mc-v {
  color: #333333;
  font-weight: 600;
  text-align: right;
  flex: 1;
  overflow-wrap: anywhere;
}

.mc-empty {
  border: 1.5px dashed #d9dbe9;
  border-radius: 14px;
  padding: 26px 18px;
  text-align: center;
  color: #6e7191;
  font-size: 13.5px;
  background: #fff;
}

.mc-empty b {
  display: block;
  color: #333333;
  font-size: 14.5px;
  margin-bottom: 4px;
}
</style>
